'use client';

import type { PropsWithChildren } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import immer from 'immer';
import { identity, noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import type { IDable } from '@frameio/utils';

export type RangeSelectionState = 'selected' | 'unselected' | 'indeterminate';

export type SelectionContext = {
  selectionSet: Set<string>;
  knownIds: Set<string>;
  idToIndex: Map<string, number>;
  orderedEntities: IDable[];
  selectedIndices: Set<number>;
  totalCount: number;
  setState: React.Dispatch<
    React.SetStateAction<Omit<SelectionContext, 'setState'>>
  >;
};

export const DEFAULT_VALUE: SelectionContext = {
  selectionSet: new Set(),
  selectedIndices: new Set(),
  knownIds: new Set(),
  idToIndex: new Map(),
  totalCount: 0,
  setState: noop,
  orderedEntities: [],
};

/**
 * @deprecated replaced by @frameio/assets/selection/contexts/SelectionContext.tsx
 *             still used for Finder 1 by the Share Reels view
 *
 * Finder 1
 * Factory to create a selection context and hook utils for updating its value. call in module scope
 */
export const createSelectionContext = () => {
  const Context = createContext(DEFAULT_VALUE);

  /**
   * wrap this provider at the lowest point in the tree such that all
   * components that need access to the selection are descendants
   * */
  const SelectionContextProvider = memo<
    PropsWithChildren<{ initialState?: Omit<SelectionContext, 'setState'> }>
  >(({ children, initialState = DEFAULT_VALUE }) => {
    const [state, setState] = useState(initialState);

    const value = useMemo<SelectionContext>(
      () => ({
        ...state,
        setState,
      }),
      [state]
    );
    return <Context.Provider value={value}>{children}</Context.Provider>;
  });

  const getIsSelected = (selectionSet: Set<string>, id: string) =>
    selectionSet.has(id);

  /** provides utils to update the set of selected ids */
  const useSetSelectedIds = () => {
    const setState = useContextSelector(Context, (context) => context.setState);

    /** select or deselect ids */
    const setSelected = useCallback(
      ({
        ids,
        deselectOthers,
        deselect,
      }: {
        /** The ids to select or deselect */
        ids: string[];
        /** Set to true to delete from selectionSet instead of add */
        deselect?: boolean;
        /** Set to true to deselect all other ids not in `ids` */
        deselectOthers?: boolean;
      }) => {
        setState(
          immer((draft) => {
            if (deselectOthers) {
              draft.selectionSet.clear();
              draft.selectedIndices.clear();
            }

            if (!deselect) {
              ids.forEach((id) => {
                draft.selectionSet.add(id);
                if (draft.orderedEntities) {
                  const idx = draft.idToIndex.get(id);
                  if (idx !== undefined) draft.selectedIndices.add(idx);
                }
              });
            } else {
              ids.forEach((id) => {
                draft.selectionSet.delete(id);
                if (draft.orderedEntities) {
                  const idx = draft.idToIndex.get(id);
                  if (idx !== undefined) draft.selectedIndices.delete(idx);
                }
              });
            }
          })
        );
      },
      [setState]
    );

    /** select or deselect an index range of `orderedEntities` */
    const setSelectedRange = useCallback(
      ({
        startIndex,
        count,
        deselect,
        deselectOthers,
      }: {
        /** the starting index in `orderedEntities */
        startIndex: number;
        /** the count after the starting index */
        count: number;
        /** set to true to delete from selectionSet instead of add */
        deselect?: boolean;
        /** set to true to deselect all other ids not in `ids` */
        deselectOthers?: boolean;
      }) => {
        setState(
          immer((draft) => {
            if (deselectOthers) {
              draft.selectionSet.clear();
              draft.selectedIndices.clear();
            }

            for (let i = startIndex; i < startIndex + count; i += 1) {
              const entity = draft.orderedEntities[i];

              if (deselect) {
                draft.selectedIndices.delete(i);
                if (entity) draft.selectionSet.delete(entity.id);
              } else {
                draft.selectedIndices.add(i);
                if (entity) draft.selectionSet.add(entity.id);
              }
            }
          })
        );
      },
      [setState]
    );

    /**
     * set the total count of items that could be selected by this context.
     * this _must_ be set if an accurate count of selected items is needed
     * when the selection mode is 'exclude'
     */
    const setTotalCount = useCallback(
      (totalCount: number) => {
        setState(
          immer((draft) => {
            draft.totalCount = totalCount;
          })
        );
      },
      [setState]
    );

    /**
     * selects all ids
     * sets "exclude" mode. this mode will represent everything as selected
     * _except_ the selection set
     */
    const selectAll = useCallback(() => {
      setState(
        immer((draft) => {
          draft.selectionSet.clear();
          draft.selectedIndices.clear();
          draft.knownIds.forEach((id) => {
            draft.selectionSet.add(id);
            if (draft.orderedEntities) {
              const idx = draft.idToIndex.get(id);
              if (idx !== undefined) draft.selectedIndices.add(idx);
            }
          });
        })
      );
    }, [setState]);

    /**
     * deselects all ids sets "include" mode. this mode will represent only ids
     * in the selection set as selected
     */
    const deselectAll = useCallback(() => {
      setState(
        immer((draft) => {
          draft.selectionSet.clear();
          draft.selectedIndices.clear();
        })
      );
    }, [setState]);

    /**
     * set the ids that are known to be subject to selection
     *
     * in a large paginated list, we may not know all the ids that could be
     * selected, but we may still need to know the set of currently known
     * selected ids. use in that case
     */
    const setKnownIds = useCallback(
      (ids: string[]) => {
        setState(
          immer((draft) => {
            draft.knownIds = new Set(ids);
          })
        );
      },
      [setState]
    );

    /**
     * set a sparse array of ordered ID'd entities for use with range selection
     */
    const setOrderedEntities = useCallback(
      (
        /** sparse array of ordered ID'd entities */
        orderedEntities: IDable[],
        /** if true, the previous range of selected indices will be maintained */
        preserveRangeSelection = false,
        /** if true, the previous known selected ids will be maintained */
        preserveIdSelection = false
      ) => {
        setState(
          immer((draft) => {
            if (!preserveRangeSelection) draft.selectedIndices.clear();
            const prevSelectionSet = new Set(draft.selectionSet);
            draft.selectionSet.clear();
            draft.idToIndex = new Map();
            draft.orderedEntities = orderedEntities;
            draft.knownIds = new Set(
              orderedEntities
                .map((entity, i) => {
                  if (
                    preserveRangeSelection &&
                    entity &&
                    draft.selectedIndices.has(i)
                  )
                    draft.selectionSet.add(entity.id);

                  if (
                    preserveIdSelection &&
                    entity &&
                    prevSelectionSet.has(entity.id)
                  ) {
                    draft.selectionSet.add(entity.id);
                    draft.selectedIndices.add(i);
                  }

                  if (entity) draft.idToIndex.set(entity.id, i);

                  return entity?.id;
                })
                .filter(identity)
            );
          })
        );
      },
      [setState]
    );

    /**
     * resets the selection set to empty and sets the mode back to default
     */
    const resetSelection = useCallback(() => {
      setState(
        immer((draft) => {
          draft.selectionSet.clear();
          draft.selectedIndices.clear();
        })
      );
    }, [setState]);

    return {
      setKnownIds,
      setOrderedEntities,
      setSelected,
      setSelectedRange,
      setTotalCount,
      deselectAll,
      selectAll,
      resetSelection,
    };
  };

  /**
   * check if a given id is selected
   * @param id the id of the entity to check if selected
   * @returns a boolean indicating if the entity is selected
   */
  const useSelectionState = (id: string) =>
    useContextSelector(Context, (ctx) => {
      const { selectionSet } = ctx;
      return getIsSelected(selectionSet, id);
    });

  /**
   * check if a given range of ids are selected
   *
   * To learn if a range of indices has been fully selected, partially selected
   * or fully deselected, `useRangeSelectionState` will provide a boolean given
   * a `startIndex` and `count` input. This is useful for getting selection
   * information about arbitrary groups and sub-groups of items in a large list.
   *
   * @param startIndex the starting index in `orderedEntities`
   * @param count the count after the starting index
   * @returns a boolean indicating if the range is selected
   */
  const useRangeSelectionState = (startIndex: number, count: number) =>
    useContextSelector(Context, (ctx): RangeSelectionState => {
      if (count === 0) return 'unselected';
      const { selectedIndices } = ctx;
      let fullySelected = true;
      let partiallySelected = false;

      for (let i = startIndex; i < startIndex + count; i += 1) {
        if (selectedIndices.has(i)) {
          partiallySelected = true;
        } else {
          fullySelected = false;
        }
      }

      if (fullySelected) return 'selected';
      if (partiallySelected) return 'indeterminate';
      return 'unselected';
    });

  /**
   * get a function to check if a particular id is selected
   * @returns a function which can be called to check to see if a particular id is selected
   */
  const useIsSelected = () => {
    const selectionSet = useContextSelector(Context, (ctx) => ctx.selectionSet);
    return useCallback(
      (id: string) => getIsSelected(selectionSet, id),
      [selectionSet]
    );
  };

  /**
   * get the set of known ids that are selected
   * @returns an array of known ids which are selected
   */
  const useKnownSelectedIds = () => {
    const knownIds = useContextSelector(Context, (ctx) => ctx.knownIds);
    const totalCount = useContextSelector(Context, (ctx) => ctx.totalCount);
    const selectionSet = useContextSelector(Context, (ctx) => ctx.selectionSet);

    const knownSelectedIds = useMemo(() => {
      return Array.from(selectionSet);
    }, [selectionSet]);

    const selectedCount = useMemo(() => selectionSet.size, [selectionSet.size]);

    return {
      knownSelectedIds,
      knownIds,
      totalCount,
      selectedCount,
      selectionSet,
    };
  };

  /**
   * get the set of selected indices and the array of ordered entities when range selection is used
   */
  const useSelectedIndices = () => {
    const selectedIndices = useContextSelector(
      Context,
      (ctx) => ctx.selectedIndices
    );
    const orderedEntities = useContextSelector(
      Context,
      (ctx) => ctx.orderedEntities
    );

    const knownSelectedIndicies = useMemo(() => {
      return selectedIndices;
    }, [selectedIndices]);

    return {
      selectedIndices: knownSelectedIndicies,
      orderedEntities,
    };
  };

  /**
   * get the selection Set which represents either the included or excluded ids
   * in the selection, depending on the selection mode
   */
  const useSelectionSet = () => {
    const selectionSet = useContextSelector(Context, (ctx) => ctx.selectionSet);

    return { selectionSet };
  };

  /**
   * get the count of selected items.
   * @note the totalItems must be set for this to be accurate in exclude mode
   */
  const useSelectionCount = () =>
    useContextSelector(Context, (ctx) => {
      const { selectionSet } = ctx;
      return selectionSet.size;
    });

  const useKnownIds = () =>
    useContextSelector(Context, (context) => context.knownIds);

  return {
    SelectionContext: Context,
    SelectionContextProvider,
    useSetSelectedIds,
    useSelectionState,
    useIsSelected,
    useKnownSelectedIds,
    useSelectionSet,
    useSelectionCount,
    useKnownIds,
    useRangeSelectionState,
    useSelectedIndices,
  };
};
